import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Trash2, Plus, Save, GripVertical, ArrowUp, ArrowDown, Users } from "lucide-react";
import { toast } from "sonner";

/** Reusable drag-to-reorder hook for vertical lists. Uses native HTML5 drag events;
 *  no external dependency. Returns props you spread onto each row. */
function useDragSort(items, setItems) {
  const onDragStart = (e, idx) => {
    e.dataTransfer.setData("text/plain", String(idx));
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
  const onDrop = (e, targetIdx) => {
    e.preventDefault();
    const src = parseInt(e.dataTransfer.getData("text/plain"), 10);
    if (isNaN(src) || src === targetIdx) return;
    const next = items.slice();
    const [moved] = next.splice(src, 1);
    next.splice(targetIdx, 0, moved);
    setItems(next);
  };
  const move = (idx, delta) => {
    const j = idx + delta;
    if (j < 0 || j >= items.length) return;
    const next = items.slice();
    [next[idx], next[j]] = [next[j], next[idx]];
    setItems(next);
  };
  return { onDragStart, onDragOver, onDrop, move };
}

function OptionList({ items, onChange, testId }) {
  const [draft, setDraft] = useState("");
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Add option..." className="h-10" data-testid={`${testId}-input`} />
        <Button type="button" onClick={() => { if (draft.trim()) { onChange([...items, draft.trim()]); setDraft(""); } }} data-testid={`${testId}-add`}><Plus className="w-4 h-4" /></Button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((it) => (
          <Badge key={it} variant="secondary" className="gap-1.5 py-1 px-2.5">
            {it}
            <button type="button" onClick={() => onChange(items.filter((x) => x !== it))} className="hover:text-red-600"><Trash2 className="w-3 h-3" /></button>
          </Badge>
        ))}
        {items.length === 0 && <p className="text-xs text-muted-foreground">No options yet.</p>}
      </div>
    </div>
  );
}

const QUESTION_TYPES = [
  { v: "text", l: "Short text" },
  { v: "textarea", l: "Long text" },
  { v: "checkbox", l: "Checkbox (Yes/No)" },
  { v: "select", l: "Single select" },
];

export default function AdminFormBuilder() {
  const [daily, setDaily] = useState({ sections: [], custom_questions: [] });
  const [appt, setAppt] = useState({ appt_types: [] });
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState(""); // "" = Global default

  useEffect(() => {
    api.get("/clients").then(({ data }) => setClients(data)).catch(() => {});
  }, []);

  useEffect(() => {
    const qs = clientId ? `?client_id=${clientId}` : "";
    api.get(`/form-configs/daily_note${qs}`).then(({ data }) => {
      const f = data?.fields || {};
      // Normalize legacy shape into sections
      if (!f.sections || !Array.isArray(f.sections)) {
        f.sections = [
          { id: "attendance_checks", title: "Attendance Checklist", options: f.attendance_checks || [] },
          { id: "activities", title: "Activities Today", options: f.activities || [] },
          { id: "mood", title: "Mood / Demeanor", options: f.mood || [] },
          { id: "meals", title: "Meals Consumed", options: f.meals || [] },
        ];
      }
      if (!f.custom_questions) f.custom_questions = [];
      setDaily(f);
    });
    api.get(`/form-configs/appointment${qs}`).then(({ data }) => data?.fields && setAppt(data.fields));
  }, [clientId]);

  const updateSection = (idx, patch) => {
    setDaily((d) => ({ ...d, sections: d.sections.map((s, i) => (i === idx ? { ...s, ...patch } : s)) }));
  };
  const removeSection = (idx) => setDaily((d) => ({ ...d, sections: d.sections.filter((_, i) => i !== idx) }));
  const addSection = () => {
    const id = `section_${Date.now()}`;
    setDaily((d) => ({ ...d, sections: [...d.sections, { id, title: "New Section", options: [] }] }));
  };

  const updateQuestion = (idx, patch) => {
    setDaily((d) => ({ ...d, custom_questions: d.custom_questions.map((q, i) => (i === idx ? { ...q, ...patch } : q)) }));
  };
  const removeQuestion = (idx) => setDaily((d) => ({ ...d, custom_questions: d.custom_questions.filter((_, i) => i !== idx) }));
  const addQuestion = () => {
    setDaily((d) => ({
      ...d,
      custom_questions: [...d.custom_questions, { id: `q_${Date.now()}`, label: "New question", type: "text", options: [] }],
    }));
  };

  const sectionSort = useDragSort(
    daily.sections,
    (next) => setDaily((d) => ({ ...d, sections: next })),
  );
  const questionSort = useDragSort(
    daily.custom_questions,
    (next) => setDaily((d) => ({ ...d, custom_questions: next })),
  );

  const saveDaily = async () => {
    // Keep legacy mirrors in sync so provider forms keep working
    const legacy = {};
    daily.sections.forEach((s) => { legacy[s.id] = s.options; });
    await api.put("/form-configs/daily_note", {
      client_id: clientId || "",
      fields: { ...daily, ...legacy },
    });
    toast.success(clientId ? "Daily Note form saved for selected client" : "Global Daily Note form saved");
  };
  const saveAppt = async () => {
    await api.put("/form-configs/appointment", { client_id: clientId || "", fields: appt });
    toast.success(clientId ? "Appointment form saved for selected client" : "Global Appointment form saved");
  };

  return (
    <div className="space-y-6" data-testid="admin-form-builder">
      <div>
        <p className="text-label">Settings</p>
        <h1 className="font-display text-3xl font-bold tracking-tight">Dynamic Form Builder</h1>
        <p className="text-sm text-muted-foreground">Rename section titles, edit options, and add brand-new custom questions. Pick a specific client to customize their form, or leave on <strong>Global Default</strong> to update the shared template.</p>
      </div>

      <Card className="p-4 flex items-center gap-3 flex-wrap bg-secondary/30" data-testid="form-builder-client-picker">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-primary" />
          <Label className="font-semibold text-sm">Customize for:</Label>
        </div>
        <Select value={clientId || "__global__"} onValueChange={(v) => setClientId(v === "__global__" ? "" : v)}>
          <SelectTrigger className="w-80" data-testid="form-builder-client-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__global__">Global Default (all clients)</SelectItem>
            {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {clientId && (
          <Badge variant="secondary" className="text-[10px]">Per-client override active</Badge>
        )}
      </Card>

      <Tabs defaultValue="daily">
        <TabsList>
          <TabsTrigger value="daily" data-testid="tab-daily">Daily Service Note</TabsTrigger>
          <TabsTrigger value="preview" data-testid="tab-preview">Contractor Preview</TabsTrigger>
          <TabsTrigger value="appt" data-testid="tab-appt">Appointment</TabsTrigger>
        </TabsList>

        <TabsContent value="daily" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={addSection} variant="outline" size="sm" data-testid="add-section"><Plus className="w-4 h-4 mr-1" /> Add Section</Button>
          </div>
          {daily.sections.map((s, idx) => (
            <Card
              key={s.id}
              className="p-5 space-y-3 cursor-default"
              draggable
              onDragStart={(e) => sectionSort.onDragStart(e, idx)}
              onDragOver={sectionSort.onDragOver}
              onDrop={(e) => sectionSort.onDrop(e, idx)}
              data-testid={`section-${s.id}`}
            >
              <div className="flex items-center gap-2">
                <span className="cursor-grab active:cursor-grabbing" title="Drag to reorder" data-testid={`section-drag-${s.id}`}>
                  <GripVertical className="w-4 h-4 text-muted-foreground" />
                </span>
                <div className="flex gap-0.5">
                  <Button
                    type="button" variant="ghost" size="sm"
                    onClick={() => sectionSort.move(idx, -1)}
                    disabled={idx === 0}
                    title="Move up"
                    className="h-7 w-7 p-0"
                    data-testid={`section-up-${s.id}`}
                  >
                    <ArrowUp className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    type="button" variant="ghost" size="sm"
                    onClick={() => sectionSort.move(idx, 1)}
                    disabled={idx === daily.sections.length - 1}
                    title="Move down"
                    className="h-7 w-7 p-0"
                    data-testid={`section-down-${s.id}`}
                  >
                    <ArrowDown className="w-3.5 h-3.5" />
                  </Button>
                </div>
                <Input
                  value={s.title}
                  onChange={(e) => updateSection(idx, { title: e.target.value })}
                  className="font-display font-semibold text-lg"
                  data-testid={`section-title-${s.id}`}
                />
                <Button variant="ghost" size="sm" onClick={() => removeSection(idx)}>
                  <Trash2 className="w-4 h-4 text-red-600" />
                </Button>
              </div>
              <OptionList items={s.options || []} onChange={(v) => updateSection(idx, { options: v })} testId={`section-opts-${s.id}`} />
            </Card>
          ))}

          <Card className="p-5 space-y-3" data-testid="custom-questions">
            <div className="flex items-center justify-between">
              <p className="text-label">Custom Questions</p>
              <Button onClick={addQuestion} size="sm" data-testid="add-question"><Plus className="w-4 h-4 mr-1" /> Add Question</Button>
            </div>
            {daily.custom_questions.length === 0 && <p className="text-xs text-muted-foreground">No custom questions yet.</p>}
            {daily.custom_questions.map((q, idx) => (
              <div
                key={q.id}
                className="border border-border rounded-md p-3 space-y-2 bg-secondary/30"
                draggable
                onDragStart={(e) => questionSort.onDragStart(e, idx)}
                onDragOver={questionSort.onDragOver}
                onDrop={(e) => questionSort.onDrop(e, idx)}
                data-testid={`question-row-${q.id}`}
              >
                <div className="flex items-center gap-2">
                  <span className="cursor-grab active:cursor-grabbing" title="Drag to reorder">
                    <GripVertical className="w-3.5 h-3.5 text-muted-foreground" />
                  </span>
                  <div className="flex gap-0.5">
                    <Button
                      type="button" variant="ghost" size="sm"
                      onClick={() => questionSort.move(idx, -1)}
                      disabled={idx === 0}
                      className="h-7 w-7 p-0"
                      data-testid={`question-up-${q.id}`}
                    >
                      <ArrowUp className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      type="button" variant="ghost" size="sm"
                      onClick={() => questionSort.move(idx, 1)}
                      disabled={idx === daily.custom_questions.length - 1}
                      className="h-7 w-7 p-0"
                      data-testid={`question-down-${q.id}`}
                    >
                      <ArrowDown className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                  <Input
                    value={q.label}
                    onChange={(e) => updateQuestion(idx, { label: e.target.value })}
                    placeholder="Question label"
                    className="flex-1"
                    data-testid={`question-label-${q.id}`}
                  />
                  <Select value={q.type} onValueChange={(v) => updateQuestion(idx, { type: v })}>
                    <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                    <SelectContent>{QUESTION_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button variant="ghost" size="sm" onClick={() => removeQuestion(idx)}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                </div>
                {q.type === "select" && (
                  <OptionList items={q.options || []} onChange={(v) => updateQuestion(idx, { options: v })} testId={`question-opts-${q.id}`} />
                )}
              </div>
            ))}
          </Card>

          <Button onClick={saveDaily} data-testid="save-daily"><Save className="w-4 h-4 mr-2" /> Save Daily Note Form</Button>
        </TabsContent>

        <TabsContent value="preview" className="space-y-4">
          <Card className="p-5 space-y-4" data-testid="contractor-preview">
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-primary/10 text-primary h-9 w-9 flex items-center justify-center shrink-0">
                <Users className="w-4 h-4" />
              </div>
              <div className="flex-1">
                <p className="text-label">What contractors will see</p>
                <p className="text-[12px] text-muted-foreground">
                  Live preview of the Daily Note form sections + custom questions you&apos;ve configured above.
                  Hard-coded compliance blocks (attendance, overnight stay, eMAR, signature, behavior triggers, interactive widgets)
                  are intentionally not editable here because they&apos;re required by Utah DSPD audit rules.
                </p>
              </div>
            </div>

            <div className="space-y-3" data-testid="preview-sections">
              {daily.sections.length === 0 && (
                <p className="text-xs text-muted-foreground italic">No sections configured.</p>
              )}
              {daily.sections.map((s) => (
                <div key={s.id} className="rounded-md border-2 border-border p-3 bg-secondary/30" data-testid={`preview-section-${s.id}`}>
                  <p className="font-display font-semibold text-sm mb-2">{s.title}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {(s.options || []).length === 0
                      ? <span className="text-[11px] text-muted-foreground italic">No options yet.</span>
                      : (s.options || []).map((o, i) => (
                          <span key={i} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white border border-border text-[11px]">
                            <input type="checkbox" disabled className="cursor-not-allowed" />
                            {o}
                          </span>
                        ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-2" data-testid="preview-custom-questions">
              <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">Custom Questions</p>
              {daily.custom_questions.length === 0 && (
                <p className="text-xs text-muted-foreground italic">No custom questions yet.</p>
              )}
              {daily.custom_questions.map((q) => (
                <div key={q.id} className="rounded-md border border-border p-3 bg-white" data-testid={`preview-question-${q.id}`}>
                  <p className="text-sm font-semibold mb-1.5">{q.label}</p>
                  {q.type === "text" && <input disabled placeholder="Short answer" className="w-full h-9 px-2 border rounded text-xs bg-secondary/30" />}
                  {q.type === "textarea" && <textarea disabled rows={2} placeholder="Long answer" className="w-full px-2 py-1 border rounded text-xs bg-secondary/30" />}
                  {q.type === "checkbox" && (
                    <label className="flex items-center gap-2 text-xs">
                      <input type="checkbox" disabled /> Yes
                    </label>
                  )}
                  {q.type === "select" && (
                    <div className="flex flex-wrap gap-1.5">
                      {(q.options || []).length === 0
                        ? <span className="text-[11px] text-muted-foreground italic">No options yet.</span>
                        : (q.options || []).map((o, i) => (
                            <span key={i} className="px-2 py-0.5 rounded-full bg-secondary/60 border border-border text-[11px]">{o}</span>
                          ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <p className="text-[11px] text-muted-foreground border-t border-border pt-3">
              Tip: Changes to sections / custom questions on the Daily Service Note tab are not live until you click <strong>Save Daily Note Form</strong>.
              Per-client overrides apply only to the client picked at the top of this page.
            </p>
          </Card>
        </TabsContent>

        <TabsContent value="appt" className="space-y-4">
          <Card className="p-5">
            <Label className="text-label mb-3 block">Appointment Types</Label>
            <OptionList items={appt.appt_types || []} onChange={(v) => setAppt({ ...appt, appt_types: v })} testId="builder-appt-types" />
          </Card>
          <Button onClick={saveAppt} data-testid="save-appt"><Save className="w-4 h-4 mr-2" /> Save Appointment Form</Button>
        </TabsContent>
      </Tabs>
    </div>
  );
}
